import dayjs from "dayjs";
import { memo, Suspense, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFontStyle } from "~/hook/useFontStyle";
import { useLanguageParams } from "~/hook/useLanguageParams";
import useLocale from "~/hook/useLocale";
import { DarshanImage } from "~/interface/daily-darshan/daily-darshan";
import { cn } from "~/lib/utils";
import { BLUR_HASH, FALLBACK_IMAGE_URL, LANG, MandirList } from "~/utils/constant";
import MandirTabs from "../daily-darshan/MandirTabs";
import ClientOnly from "../shared-components/ClentOnly";
import NodataFound from "../shared-components/NoDataFound";
import DailyDarshanCarousel from "./DailyDarshanCarousel";
import DailyDarshanHomePhotosMobile from "./DailyDarshanHomePhotosMobile";

interface DailyDarshanProps {
  dailyDarshan: Record<string, DarshanImage[]>;
  // availableDataDatesArray: string[];
}

export const DEFAULT_SELECTED_MANDIR = "karelibaug";

const DailyDarshanHome = memo(function DailyDarshanHome({ dailyDarshan }: DailyDarshanProps) {
  const { t } = useTranslation();
  const { medium, normalInter } = useFontStyle();
  const lang = useLanguageParams();
  const isGujarati = lang === LANG.gujarati;

  const { LocalizedLink } = useLocale();
  const [width, setWidth] = useState<number>(0);
  const rowHeight = useMemo(() => {
    return width <= 425 ? 400 : width >= 768 ? 350 : width > 1024 ? 550 : 200;
  }, [width]);

  const [selectedMandir, setSelectedMandir] = useState(DEFAULT_SELECTED_MANDIR);
  const [dailyDarshanData, setDailyDarshanData] = useState<DarshanImage[]>(dailyDarshan[DEFAULT_SELECTED_MANDIR] ?? []);
  const [globalDate, setGlobalDate] = useState(dayjs().format("YYYY-MM-DD"));

  // Memoize image mapping to prevent carousel re-renders
  const mappedImages = useMemo(
    () =>
      dailyDarshanData?.map((data) => ({
        id: data.id,
        url:
          data.type === "image" && data?.img
            ? {
                small: data.img.s.url,
                medium: data.img.m.url,
                large: data.img.l.url,
                hd: data.img.l.url,
                original: data.img.og.url,
              }
            : {
                small: FALLBACK_IMAGE_URL,
                medium: FALLBACK_IMAGE_URL,
                large: FALLBACK_IMAGE_URL,
                hd: FALLBACK_IMAGE_URL,
                original: FALLBACK_IMAGE_URL,
              },
        alt: `${selectedMandir} ${data.date}`,
        blur_hash: data.img ? data.img.bh : BLUR_HASH,
        img_height: data.img ? data.img.ar : 500,
        video_id: data.type === "yt" ? data?.original_url : null,
      })) ?? [],
    [dailyDarshanData, selectedMandir],
  );

  // Window resize with debounce
  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    handleResize();
    let debounceTimer: NodeJS.Timeout;
    const debouncedResize = () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => setWidth(window.innerWidth), 300);
    };
    window.addEventListener("resize", debouncedResize);
    return () => {
      window.removeEventListener("resize", debouncedResize);
      clearTimeout(debounceTimer);
    };
  }, []);

  // Sync data when mandir changes
  useEffect(() => {
    if (selectedMandir) {
      setDailyDarshanData(dailyDarshan[selectedMandir] ?? []);
    }
  }, [selectedMandir, dailyDarshan]);

  return (
    <div className="w-full flex flex-col justify-start items-center gap-btw-text-and-content">
      <div className="w-full flex flex-col justify-center items-center gap-6">
        <div className="flex flex-col justify-center items-center gap_btw_title_content">
          <LocalizedLink to={"/daily-darshan"} className={cn(isGujarati ? "home-page-heading-mar-guj" : "home-page-heading", medium)}>
            {t("home.dailyDarshan")}
          </LocalizedLink>
          <span className={cn("sub_desc_text_color body_text_l text-center title_para_width ", normalInter)}>
            {t("home.dailyDarshanDescription", {
              defaultValue:
                "Experience daily darshan from Shri Swaminarayan Mandir, Karelibaug-Vadodara, and the six temples (Dham) established by Bhagwan Swaminarayan.",
            })}
          </span>
        </div>
        <div className="w-full flex flex-col justify-start items-center gap-1 lg:gap-2">
          <MandirTabs mandir={MandirList} setSelectedMandir={setSelectedMandir} selectedMandir={selectedMandir} />
        </div>
      </div>

      <div className="w-full flex flex-col justify-start items-center">
        {/* Desktop View */}
        <div className="w-full hidden md:flex">
          {dailyDarshanData?.length > 0 ? (
            <DailyDarshanHomePhotosMobile dailyDarshanData={dailyDarshanData} selectedMandir={selectedMandir} rowHeight={rowHeight} />
          ) : (
            <div className="w-full flex justify-center items-center">
              <NodataFound text={t("daily-darshan.no_data_for_date")} />
            </div>
          )}
        </div>

        {/* Mobile View — carousel uses browser-only APIs (ReactPlayer, Embla autoplay) */}
        <div className="w-full flex md:hidden">
          {dailyDarshanData?.length === 0 ? (
            <NodataFound text={t("daily-darshan.no_data_for_date")} />
          ) : (
            <ClientOnly fallback={<div className="w-full h-[400px] bg-secondaryColor animate-pulse rounded-md" />}>
              <Suspense fallback={<div className="w-full h-[400px] bg-secondaryColor animate-pulse rounded-md" />}>
                <DailyDarshanCarousel images={mappedImages} />
              </Suspense>
            </ClientOnly>
          )}
        </div>
      </div>
    </div>
  );
});

export default DailyDarshanHome;
